import React, { useCallback } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import { Checkbox, Chip, IconButton, Stack, Typography } from '@mui/material';

import { ConflictResolution, ExistingBlackoutDate, ManualRoom } from '~/types/stopsell';

import { findRateConflict } from '~/utils/stopsellConflicts';
import { countNights, formatDateRange } from '~/utils/stopsellDateUtils';
import { pluralizeToString } from '~/utils/stringUtils';

import StopsellRateRow from './StopsellRateRow';

interface Props {
  date: ManualRoom['dates'][number];
  dateIndex: number;
  catalogRoomRates: Array<{ id: string; name: string }>;
  blackoutDates: Array<ExistingBlackoutDate>;
  rateResolutions: Record<string, ConflictResolution>;
  onRemoveDate: (dateIndex: number) => void;
  onToggleDate: (dateIndex: number, selected: boolean) => void;
  onResolve: (dateIndex: number, rateId: string, status: ConflictResolution) => void;
  onUndo: (dateIndex: number, rateId: string) => void;
}

export default function StopsellManualRoomDateRow({
  date,
  dateIndex,
  catalogRoomRates,
  blackoutDates,
  rateResolutions,
  onRemoveDate,
  onToggleDate,
  onResolve,
  onUndo,
}: Props) {
  const handleRemove = useCallback(() => onRemoveDate(dateIndex), [onRemoveDate, dateIndex]);
  const handleToggle = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onToggleDate(dateIndex, e.target.checked),
    [onToggleDate, dateIndex],
  );

  return (
    <Stack gap={0.5}>
      <Stack direction="row" alignItems="center" gap={1}>
        <Checkbox size="small" checked={date.selected} onChange={handleToggle} />
        <Typography variant="body2" color="text.secondary">
          {formatDateRange(date.start, date.end)}
        </Typography>
        <Chip label={pluralizeToString('night', countNights(date.start, date.end))} size="small" />
        <IconButton size="small" onClick={handleRemove}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Stack>

      {catalogRoomRates.length > 0 && date.selected && (
        <Stack gap={0.5} sx={{ pl: 4 }}>
          {catalogRoomRates.map((rate) => {
            const conflict = findRateConflict(date, rate.id, blackoutDates);
            const resolution = rateResolutions[`${dateIndex}-${rate.id}`] ?? null;
            return (
              <StopsellRateRow
                key={rate.id}
                rateName={rate.name}
                rateConflict={conflict}
                resolution={resolution}
                onResolve={(status) => onResolve(dateIndex, rate.id, status)}
                onUndo={() => onUndo(dateIndex, rate.id)}
              />
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}
